"use client";

import { useMemo, useState } from "react";
import { Calendar, CalendarCheck, CheckCircle, CreditCard, Loader2 } from "lucide-react";

import type {
  iConfirmBookingBlockData,
  iCTABlockData,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { SDK } from "@/@sdk";
import { formatSafeQuoteDate } from "@/components/quotes/helpers/format-safe-date";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { quoteThemeColorStyles, quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { QuoteDepositPayButton } from "./quote-deposit-pay-button";
import {
  collectQuotePricingDepositOptions,
  formatQuoteCurrency,
  resolveBookingBlockOptions,
  resolveQuoteLegalUrls,
  type QuotePricingDepositOption,
} from "./quote-booking-options.helpers";

type QuoteBookingBlockData = iCTABlockData | iConfirmBookingBlockData;

interface QuoteBookingConfirmationOptionsProps {
  blockData: QuoteBookingBlockData;
  depositAmount?: number;
  depositDueDate?: number | string;
  primaryColor?: string;
  onBookingConfirmed?: () => void;
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-1 border-t border-gray-200" />
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
        or
      </span>
      <div className="flex-1 border-t border-gray-200" />
    </div>
  );
}

function TermsAgreement({
  agreedToTerms,
  onChange,
  termsAndConditionsUrl,
  bookingConditionsUrl,
  primaryColor,
  required = true,
}: {
  agreedToTerms: boolean;
  onChange: (value: boolean) => void;
  termsAndConditionsUrl?: string;
  bookingConditionsUrl?: string;
  primaryColor: string;
  required?: boolean;
}) {
  const hasLinks = Boolean(termsAndConditionsUrl || bookingConditionsUrl);

  return (
    <label className="flex items-start space-x-2">
      <input
        type="checkbox"
        checked={agreedToTerms}
        onChange={(e) => onChange(e.target.checked)}
        required={required}
        className="mt-1"
        style={{ accentColor: quoteThemeCssVars.primary }}
      />
      <span className="text-xs sm:text-sm leading-relaxed text-gray-700">
        {hasLinks ? (
          <>
            I have read and agree to the{" "}
            {termsAndConditionsUrl ? (
              <a
                href={termsAndConditionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:no-underline"
                style={quoteThemeColorStyles.primary}
              >
                Terms and Conditions
              </a>
            ) : null}
            {termsAndConditionsUrl && bookingConditionsUrl ? " and " : null}
            {bookingConditionsUrl ? (
              <a
                href={bookingConditionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:no-underline"
                style={quoteThemeColorStyles.primary}
              >
                Booking Conditions
              </a>
            ) : null}
          </>
        ) : (
          "I agree to proceed with this booking"
        )}
      </span>
    </label>
  );
}

function StripePlanPicker({
  options,
  selectedBlockId,
  onSelect,
  primaryColor,
}: {
  options: QuotePricingDepositOption[];
  selectedBlockId: string;
  onSelect: (blockId: string) => void;
  primaryColor: string;
}) {
  const selected =
    options.find((opt) => opt.blockId === selectedBlockId) ?? options[0];

  if (options.length === 1 && selected) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl text-center"
        style={quoteThemeColorStyles.primary10Bg}
      >
        <div
          className="absolute inset-0"
          style={quoteThemeColorStyles.diagonalGradientSurface}
        />
        <div className="relative px-5 py-6 sm:px-8 sm:py-8 space-y-4">
          {selected.name ? (
            <span className="inline-block rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700 shadow-sm">
              {selected.name}
            </span>
          ) : null}
          <div>
            <p className="text-sm font-medium text-gray-600 mb-2">
              Pay today to secure your booking
            </p>
            <p
              className="text-4xl sm:text-5xl font-bold tracking-tight"
              style={quoteThemeColorStyles.primary}
            >
              {formatQuoteCurrency(
                selected.depositRequired,
                selected.currency
              )}
            </p>
            <p className="text-sm text-gray-500 mt-2">deposit due now</p>
          </div>
          <div className="pt-4 border-t border-white/70">
            <p className="text-sm text-gray-600">
              Total holiday price{" "}
              <span className="font-semibold text-gray-900">
                {formatQuoteCurrency(selected.totalPrice, selected.currency)}
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-800">Select your plan</p>
      <div className="space-y-2.5">
        {options.map((opt) => {
          const isSelected = opt.blockId === selectedBlockId;
          return (
            <button
              key={opt.blockId}
              type="button"
              onClick={() => onSelect(opt.blockId)}
              className={`w-full text-left rounded-xl border-2 p-4 transition-all duration-200 ${
                isSelected
                  ? "shadow-md"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
              }`}
              style={
                isSelected
                  ? {
                      borderColor: quoteThemeCssVars.primary,
                      backgroundColor: quoteThemeCssVars.primary10,
                    }
                  : undefined
              }
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{opt.name}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Total holiday price{" "}
                    {formatQuoteCurrency(opt.totalPrice, opt.currency)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                    Deposit today
                  </p>
                  <p
                    className="text-xl font-bold leading-tight"
                    style={quoteThemeColorStyles.primary}
                  >
                    {formatQuoteCurrency(opt.depositRequired, opt.currency)}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function QuoteBookingConfirmationOptions({
  blockData,
  depositAmount,
  depositDueDate,
  primaryColor: _legacyPrimaryColor,
  onBookingConfirmed,
}: QuoteBookingConfirmationOptionsProps) {
  const { quote, isAdminMode, quoteLegalConfig } = useHolidayQuoteBrand();
  void _legacyPrimaryColor;

  const options = resolveBookingBlockOptions(blockData);
  const legalUrls = resolveQuoteLegalUrls(quoteLegalConfig, blockData);
  const pricingOptions = useMemo(
    () => collectQuotePricingDepositOptions(quote),
    [quote]
  );

  const [stripeAgreed, setStripeAgreed] = useState(false);
  const [instantAgreed, setInstantAgreed] = useState(false);
  const [deferredAgreed, setDeferredAgreed] = useState(false);
  const [paymentDate, setPaymentDate] = useState("");
  const [selectedPricingBlockId, setSelectedPricingBlockId] = useState(
    pricingOptions[0]?.blockId ?? ""
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const selectedPricing =
    pricingOptions.find((opt) => opt.blockId === selectedPricingBlockId) ??
    pricingOptions[0];

  const showStripeOption =
    options.includeBookingConfirmationViaStripeDeposit &&
    pricingOptions.length > 0;

  const enabledSections = [
    showStripeOption,
    options.includeBookingConfirmation,
    options.includeDeferredBookingConfirmation,
  ].filter(Boolean);

  const confirmBooking = async (withPaymentDate: boolean) => {
    setConfirmError(null);
    setIsConfirming(true);

    try {
      if (isAdminMode) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        onBookingConfirmed?.();
        return;
      }

      await SDK.holidayQuotesX.confirmBooking(quote.id!, {
        ...(withPaymentDate && paymentDate ? { paymentDate } : {}),
        agreedToTerms: true,
      });

      onBookingConfirmed?.();
    } catch (error) {
      setConfirmError(
        "Something went wrong with your booking. Please email us to proceed."
      );
      console.error("Booking error:", error);
    } finally {
      setIsConfirming(false);
    }
  };

  if (enabledSections.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No booking confirmation options are configured for this block.
      </p>
    );
  }

  const depositSummary =
    depositAmount && depositDueDate ? (
      <div
        className="inline-flex items-center gap-2 p-3 rounded-xl"
        style={quoteThemeColorStyles.primary10Bg}
      >
        <Calendar className="w-4 h-4" style={quoteThemeColorStyles.primary} />
        <p className="text-sm sm:text-base" style={quoteThemeColorStyles.primary}>
          Deposit amount: £{depositAmount} due by{" "}
          {formatSafeQuoteDate(depositDueDate)}
        </p>
      </div>
    ) : null;

  const sections: React.ReactNode[] = [];

  if (showStripeOption && selectedPricing) {
    sections.push(
      <div
        key="stripe"
        className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 sm:p-5 space-y-4"
      >
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" style={quoteThemeColorStyles.primary} />
          <h3 className="font-semibold text-gray-900">Pay deposit now</h3>
        </div>
        <p className="text-sm text-gray-600">
          Pay your deposit securely online to confirm your place.
        </p>

        <StripePlanPicker
          options={pricingOptions}
          selectedBlockId={selectedPricingBlockId}
          onSelect={setSelectedPricingBlockId}
          primaryColor={quoteThemeCssVars.primary}
        />

        <TermsAgreement
          agreedToTerms={stripeAgreed}
          onChange={setStripeAgreed}
          termsAndConditionsUrl={legalUrls.termsAndConditionsUrl}
          bookingConditionsUrl={legalUrls.bookingConditionsUrl}
          primaryColor={quoteThemeCssVars.primary}
        />

        <QuoteDepositPayButton
          quoteId={quote.id!}
          pricingBlockId={selectedPricing.blockId}
          depositAmount={selectedPricing.depositRequired}
          currency={selectedPricing.currency}
          primaryColor={quoteThemeCssVars.primary}
          disabled={!stripeAgreed}
        />
      </div>
    );
  }

  if (options.includeBookingConfirmation) {
    sections.push(
      <div
        key="instant"
        className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 sm:p-5 space-y-4"
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5" style={quoteThemeColorStyles.primary} />
          <h3 className="font-semibold text-gray-900">Confirm booking now</h3>
        </div>
        <p className="text-sm text-gray-600">
          Confirm your booking immediately — we&apos;ll be in touch with next steps.
        </p>
        {depositSummary}
        <TermsAgreement
          agreedToTerms={instantAgreed}
          onChange={setInstantAgreed}
          termsAndConditionsUrl={legalUrls.termsAndConditionsUrl}
          bookingConditionsUrl={legalUrls.bookingConditionsUrl}
          primaryColor={quoteThemeCssVars.primary}
        />
        <button
          type="button"
          onClick={() => confirmBooking(false)}
          disabled={isConfirming || !instantAgreed}
          className="w-full py-3 px-4 rounded-xl text-white text-sm sm:text-base font-semibold transition-all duration-200 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
          style={quoteThemeColorStyles.primaryBg}
        >
          {isConfirming ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Confirm booking
            </span>
          )}
        </button>
      </div>
    );
  }

  if (options.includeDeferredBookingConfirmation) {
    sections.push(
      <div
        key="deferred"
        className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 sm:p-5 space-y-4"
      >
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5" style={quoteThemeColorStyles.primary} />
          <h3 className="font-semibold text-gray-900">Confirm with deposit date</h3>
        </div>
        <p className="text-sm text-gray-600">
          Tell us when you plan to pay the deposit and confirm your booking.
        </p>
        {depositSummary}
        <div>
          <label className="block text-sm font-medium mb-2">
            When would you like to pay the deposit?
          </label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="w-full p-2 sm:p-3 border rounded-xl text-sm sm:text-base focus:outline-none focus:ring-2 transition-all duration-200"
            style={
              {
                borderColor: quoteThemeCssVars.primary10,
                "--tw-ring-color": quoteThemeCssVars.primary,
              } as React.CSSProperties
            }
            required
          />
        </div>
        <TermsAgreement
          agreedToTerms={deferredAgreed}
          onChange={setDeferredAgreed}
          termsAndConditionsUrl={legalUrls.termsAndConditionsUrl}
          bookingConditionsUrl={legalUrls.bookingConditionsUrl}
          primaryColor={quoteThemeCssVars.primary}
        />
        <button
          type="button"
          onClick={() => confirmBooking(true)}
          disabled={isConfirming || !deferredAgreed || !paymentDate}
          className="w-full py-3 px-4 rounded-xl text-white text-sm sm:text-base font-semibold transition-all duration-200 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
          style={quoteThemeColorStyles.primaryBg}
        >
          {isConfirming ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <CalendarCheck className="h-4 w-4" />
              Proceed with booking
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {confirmError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {confirmError}
        </div>
      ) : null}
      {sections.map((section, index) => (
        <div key={index}>
          {section}
          {index < sections.length - 1 ? <OrDivider /> : null}
        </div>
      ))}
    </div>
  );
}
